import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import CardReader from '../../components/CardReader';
import CardAndHand from '../../components/CardAndHand';
import {
  TINY_BREAKPOINT_IN_PX,
  MOBILE_BREAKPOINT_IN_PX,
  TABLET_BREAKPOINT_IN_PX,
  LAPTOP_BREAKPOINT_IN_PX,
  MOBILE_SIZE_IN_PERCENTAGE,
  TABLET_SIZE_IN_PERCENTAGE,
  LAPTOP_SIZE_IN_PERCENTAGE,
} from '../../constants';

const CARD_READER_MAX_HEIGHT_IN_PX = 720;
const CARD_AND_HAND_BOTTOM_VALUE_IN_PX = -70;
const CARD_AND_HAND_LEFT_VALUE_IN_PX = 360;

const getCardAndHandBottomPosition = (isHomeScreen, zoomLevel) => {
  if (isHomeScreen) {
    return CARD_AND_HAND_BOTTOM_VALUE_IN_PX * MOBILE_SIZE_IN_PERCENTAGE;
  }

  if (!isHomeScreen && zoomLevel === 2) {
    return '0';
  }

  return '100';
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 1440px;
  margin: 0 auto;
  position: relative;
  border-bottom: ${(props) =>
    props.isHomeScreen ? '1px solid #ccc;' : 'none;'};

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    min-height: 640px;
    flex-direction: column;
  }

  @media screen and (max-width: ${TINY_BREAKPOINT_IN_PX}px) {
    min-height: 640px;
  }
`;

// prettier-ignore
const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    width: auto;
    justify-content: center;

    ${(props) => props.isHomeScreen && css`
      & > div:first-child {
        position: relative;
      }
    `};
  }

  ${(props) => !props.isHomeScreen && props.isFullScreenModeEnabled && css`
    @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {  
      display: none;
      pointer-events: none;
    }
  `};
`;

// prettier-ignore
const CardReaderColumn = styled.div`
  display: flex;
  width: 50%;
  box-sizing: border-box;
  align-items: flex-start;
  padding-left: 60px;
  max-height: ${CARD_READER_MAX_HEIGHT_IN_PX}px;
  overflow: hidden;
  position: relative;

  @media screen and (max-width: ${LAPTOP_BREAKPOINT_IN_PX}px) {
    padding-left: 15px;
  }

  @media screen and (max-width: ${TABLET_BREAKPOINT_IN_PX}px) {
    padding-left: 0;
    max-height: ${CARD_READER_MAX_HEIGHT_IN_PX * TABLET_SIZE_IN_PERCENTAGE}px;
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    width: 100%;
    height: 100%;
    max-height: ${(props) => props.isHomeScreen ? '445px;' : '555px;'};
    display: flex;
    justify-content: center;
    transition: opacity 0.5s;
    opacity: 1;
    flex: 1;
    border-top: 1px solid #ccc;
  }

  ${(props) => props.isFullScreenModeEnabled && css`
    @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
      max-height: 780px;
    }
  `};
`;

// prettier-ignore
const CardAndHandWrapper = styled.div`
  position: absolute;
  bottom: ${CARD_AND_HAND_BOTTOM_VALUE_IN_PX}px;
  left: ${CARD_AND_HAND_LEFT_VALUE_IN_PX}px;
  pointer-events: none;

  @media screen and (max-width: ${LAPTOP_BREAKPOINT_IN_PX}px) {
    bottom: ${CARD_AND_HAND_BOTTOM_VALUE_IN_PX * LAPTOP_SIZE_IN_PERCENTAGE}px;
    left: ${CARD_AND_HAND_LEFT_VALUE_IN_PX * LAPTOP_SIZE_IN_PERCENTAGE}px;
  }

  @media screen and (max-width: ${TABLET_BREAKPOINT_IN_PX}px) {
    bottom: ${CARD_AND_HAND_BOTTOM_VALUE_IN_PX * TABLET_SIZE_IN_PERCENTAGE}px;
    left: ${CARD_AND_HAND_LEFT_VALUE_IN_PX * TABLET_SIZE_IN_PERCENTAGE - 45}px;
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    bottom: ${(props) => getCardAndHandBottomPosition(props.isHomeScreen, props.zoomLevel)}px;
    left: 63%;
  }

  ${(props) => props.isFullScreenModeEnabled && css`
    @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
      bottom: ${CARD_AND_HAND_BOTTOM_VALUE_IN_PX}px;
      left: 50%;
    }
  `};
`;

const CardReaderView = ({
  textColumnContent,
  cardReaderColumnContent,
  cardReaderZoomLevel,
  cardReaderGreenLightActive,
  cardReaderCardLightActive,
  cardPosition,
  isFullScreenModeEnabled,
  isHomeScreen,
}) => (
  <Wrapper isHomeScreen={isHomeScreen}>
    <TextColumn
      isHomeScreen={isHomeScreen}
      isFullScreenModeEnabled={isFullScreenModeEnabled}
    >
      {textColumnContent}
    </TextColumn>
    <CardReaderColumn
      isHomeScreen={isHomeScreen}
      isFullScreenModeEnabled={isFullScreenModeEnabled}
    >
      <CardReader
        isHomeScreen={isHomeScreen}
        zoomLevel={cardReaderZoomLevel}
        greenLightActive={cardReaderGreenLightActive}
        cardLightActive={cardReaderCardLightActive}
        isFullScreenModeEnabled={isFullScreenModeEnabled}
      >
        {cardReaderColumnContent}
      </CardReader>
      <CardAndHandWrapper
        isHomeScreen={isHomeScreen}
        zoomLevel={cardReaderZoomLevel}
        cardPosition={cardPosition}
        isFullScreenModeEnabled={isFullScreenModeEnabled}
      >
        <CardAndHand
          position={cardPosition}
          isFullScreenModeEnabled={isFullScreenModeEnabled}
        />
      </CardAndHandWrapper>
    </CardReaderColumn>
  </Wrapper>
);

CardReaderView.propTypes = {
  textColumnContent: PropTypes.node.isRequired,
  cardReaderColumnContent: PropTypes.node.isRequired,
  cardReaderZoomLevel: PropTypes.number.isRequired,
  cardReaderGreenLightActive: PropTypes.bool.isRequired,
  cardReaderCardLightActive: PropTypes.bool.isRequired,
  cardPosition: PropTypes.number.isRequired,
  isFullScreenModeEnabled: PropTypes.bool.isRequired,
  isHomeScreen: PropTypes.bool.isRequired,
};

export default CardReaderView;
